import { h as __h } from "preact";
import { Component, h } from "preact";
/** @import { TargetedMouseEvent } from "preact"; */

import { adminMachineResponse, connectionResponse } from "~/backend/validate.js";
/** @import WS from "~/lib/ws/ws.js"; */
import { paginatedResponse, query, response } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import * as msg from "~/lib/ws/msg.js";
import html from "~/util/html.js";
import { navigate } from "~/util/router.js";
import Title from "~/util/title.js";
import AgentIcon from "~/component/AgentIcon.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import Loader from "~/component/Loader.js";
import Time, { duration, relative, timestamp } from "~/component/Time.js";
import Tooltip from "~/component/Tooltip.js";
/** @import { TriggerProps } from "~/component/Tooltip.js"; */
import WSListener from "~/component/WSListener.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import ObjectPage, { ObjectPageSection } from "~/admin/component/ObjectPage.js";
import Pagination from "~/admin/component/Pagination.js";
import { StatusChip } from "~/admin/component/StatusChip.js";
import Table from "~/admin/component/Table.js";
import { connect } from "~/admin/res/computer/actions/connect.js";
import { deleteComputers } from "~/admin/res/computer/actions/deleteComputers.js";
import { renameComputer } from "~/admin/res/computer/actions/renameComputer.js";
import { setAssignee } from "~/admin/res/computer/actions/setAssignee.js";
import { setLocation } from "~/admin/res/computer/actions/setLocation.js";
import GroupChip from "~/admin/res/group/GroupChip.js";
import LocationChip from "~/admin/res/location/LocationChip.js";
import MemberChip from "~/admin/res/member/MemberChip.js";
import util from "~/style/util.js";
/** @import { orgResponse } from "~/backend/validate.js"; */

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {orgResponse} org
 * @property {string} machineId
 * @property {WS} ws
 */
export default class ComputersDetail extends Component {
  machineLookup = query(() => ({
    key: [`/ui/admin/orgs/${this.props.org.id}/machines/${this.props.machineId}`],
    keepPreviousData: true
  }), {
    schema: response(adminMachineResponse)
  });
  render() {
    const machine = this.machineLookup.data?.data;
    return [__h(Crumb, {}, machine?.name ?? __h(Loader, {})), machine && __h(Title, {
      title: machine.name
    }), __h(Data, {
      loading: this.machineLookup.loading && !this.machineLookup.data,
      error: this.machineLookup.error,
      empty: Boolean(this.machineLookup.data) && !machine
    }, machine && this.renderPage(machine)), __h(WSListener, {
      ws: this.props.ws,
      "for": [msg.ADMIN_MACHINE_REFRESH],
      on: () => this.machineLookup.fetch()
    })];
  }

  /** @param {adminMachineResponse} machine */
  renderPage(machine) {
    const canConnect = identity.permission(this.props.org.id, "machine_connect");
    const canEdit = identity.permission(this.props.org.id, "machines", "edit");
    return __h(ObjectPage, {
      backlink: {
        href: `/org/${this.props.org.id}/computers`,
        name: "Computers"
      },
      title: [__h(Icon, {
        name: "machine",
        size: 22
      }), __h("span", {}, machine.name)],
      actions: [canConnect && __h(Button, {
        level: "primary",
        size: "small",
        disabled: !machine.is_online,
        onClick: () => connect(machine.id)
      }, "Connect"), canEdit && [__h(Button, {
        size: "small",
        onClick: () => renameComputer({
          orgId: this.props.org.id
        }, machine)
      }, __h(Icon, {
        name: "pencil",
        size: 12
      }), "Rename"), __h(Button, {
        size: "small",
        kind: "negative",
        onClick: async () => {
          const res = await deleteComputers({
            orgId: this.props.org.id
          }, machine.id);
          if (res) navigate(`/org/${this.props.org.id}/computers`);
        }
      }, __h(Icon, {
        name: "trash",
        size: 12
      }), "Remove")]],
      sidebar: [__h(ObjectPageSection, {
        title: "Status"
      }, __h(StatusChip, {
        online: machine.is_online
      })), __h(ObjectPageSection, {
        title: "Hostname"
      }, machine.hostname), __h(ObjectPageSection, {
        title: "Version"
      }, machine.version), __h(ObjectPageSection, {
        title: "Channel"
      }, machine.channel), __h(ObjectPageSection, {
        title: "Assigned to",
        actions: canEdit && __h(Button, {
          size: "small",
          level: "tertiary",
          onClick: (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => setAssignee({
            anchor: event.currentTarget,
            placement: "bottom",
            orgId: this.props.org.id
          }, machine)
        }, __h(Icon, {
          name: "pencil",
          size: 12
        }), "Edit")
      }, machine.user && __h(MemberChip, {
        member: {
          ...machine.user,
          org_id: this.props.org.id
        }
      }), machine.group && __h(GroupChip, {
        group: {
          ...machine.group,
          org_id: this.props.org.id
        }
      })), __h(ObjectPageSection, {
        title: "Location",
        actions: canEdit && __h(Button, {
          size: "small",
          level: "tertiary",
          onClick: (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => setLocation({
            anchor: event.currentTarget,
            placement: "bottom",
            orgId: this.props.org.id
          }, machine)
        }, __h(Icon, {
          name: "pencil",
          size: 12
        }), "Edit")
      }, machine.location && __h(LocationChip, {
        location: {
          ...machine.location,
          org_id: this.props.org.id
        }
      }))]
    }, __h(ObjectPageSection, {
      title: "Active connections"
    }, __h("div", {
      "class": util.card
    }, __h(ActiveConnections, {
      org: this.props.org,
      machineId: this.props.machineId,
      ws: this.props.ws
    }))), __h(ObjectPageSection, {
      title: "Past connections"
    }, __h("div", {
      "class": util.card
    }, __h(PastConnections, {
      org: this.props.org,
      machineId: this.props.machineId,
      ws: this.props.ws
    }))));
  }
}

/**
 * @extends {Component<ActiveConnectionsProps>}
 *
 * @typedef {object} ActiveConnectionsProps
 * @property {orgResponse} org
 * @property {string} machineId
 * @property {WS} ws
 */
class ActiveConnections extends Component {
  paginate = new Pagination.State({
    size: 5,
    local: true
  });
  sort = new Table.Sort({
    keys: ["connected_at", "last_heartbeat_at", "disconnected_at"],
    by: "connected_at",
    order: "desc",
    local: true
  });
  connections = query(() => ({
    key: [`/v2/orgs/${this.props.org.id}/connections`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      machine_id: this.props.machineId,
      is_active: true
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(connectionResponse)
  });
  render() {
    const rows = this.connections.data?.data.map(conn => {
      const connectedAt = conn.connected_at && new Date(conn.connected_at);
      return {
        key: conn.id,
        label: `connection by ${conn.user?.name}${conn.control_type === "mcp" ? " (agent)" : ""}`,
        value: conn,
        columns: {
          member: [conn.user && __h(MemberChip, {
            member: {
              ...conn.user,
              org_id: this.props.org.id
            }
          }), conn.control_type === "mcp" && __h(AgentIcon, {})],
          connectedAt: connectedAt && __h(Tooltip, {
            contents: __h(Time, {
              tick: true,
              fmt: relative,
              datetime: connectedAt
            })
          }, (/** @type {TriggerProps} */trigger) => __h("button", trigger, __h(Time, {
            fmt: timestamp,
            datetime: connectedAt
          }))),
          duration: connectedAt && __h(Time, {
            tick: true,
            fmt: duration,
            datetime: connectedAt
          }),
          lastHeartbeat: conn.last_heartbeat_at && __h(Time, {
            tick: true,
            fmt: relative,
            datetime: new Date(conn.last_heartbeat_at)
          })
        }
      };
    });
    return [h(Data, {
      loading: this.connections.loading && !this.connections.data,
      error: this.connections.error,
      children: h(/** @type {typeof Table<connectionResponse>} */Table, {
        label: "active connection",
        headers: [{
          id: "member",
          label: "Member",
          empty: "Unknown user"
        }, {
          id: "duration",
          label: "Duration",
          empty: "None"
        }, {
          id: "connectedAt",
          label: "Connected at",
          empty: "Never",
          sort: "connected_at"
        }, {
          id: "lastHeartbeat",
          label: "Last heartbeat",
          empty: "Never",
          sort: "last_heartbeat_at"
        }],
        empty: "No one is currently connected to this machine.",
        rows: rows ?? [],
        paginate: this.paginate.props(this.connections),
        sort: this.sort.props()
      })
    }), __h(WSListener, {
      ws: this.props.ws,
      "for": [msg.ADMIN_CONNECTION_REFRESH],
      on: () => this.connections.fetch()
    })];
  }
}

/**
 * @extends {Component<PastConnectionsProps>}
 *
 * @typedef {object} PastConnectionsProps
 * @property {orgResponse} org
 * @property {string} machineId
 * @property {WS} ws
 */
class PastConnections extends Component {
  paginate = new Pagination.State({
    size: 5,
    local: true
  });
  sort = new Table.Sort({
    keys: ["connected_at", "last_heartbeat_at", "disconnected_at"],
    by: "disconnected_at",
    order: "desc",
    local: true
  });
  connections = query(() => ({
    key: [`/v2/orgs/${this.props.org.id}/connections`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      machine_id: this.props.machineId,
      is_active: false
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(connectionResponse)
  });
  render() {
    const rows = this.connections.data?.data.map(conn => {
      const connectedAt = conn.connected_at && new Date(conn.connected_at);
      const disconnectedAt = conn.disconnected_at && new Date(conn.disconnected_at);
      return {
        key: conn.id,
        label: `connection by ${conn.user?.name}${conn.control_type === "mcp" ? " (agent)" : ""}`,
        value: conn,
        columns: {
          member: [conn.user && __h(MemberChip, {
            member: {
              ...conn.user,
              org_id: this.props.org.id
            }
          }), conn.control_type === "mcp" && __h(AgentIcon, {})],
          connectedAt: connectedAt && __h(Tooltip, {
            contents: __h(Time, {
              tick: true,
              fmt: relative,
              datetime: connectedAt
            })
          }, (/** @type {TriggerProps} */trigger) => __h("button", trigger, __h(Time, {
            fmt: timestamp,
            datetime: connectedAt
          }))),
          duration: connectedAt && disconnectedAt && __h(Time, {
            fmt: duration,
            datetime: connectedAt,
            now: disconnectedAt
          }),
          disconnectedAt: disconnectedAt && __h(Tooltip, {
            contents: __h(Time, {
              tick: true,
              fmt: relative,
              datetime: disconnectedAt
            })
          }, (/** @type {TriggerProps} */trigger) => __h("button", trigger, __h(Time, {
            fmt: timestamp,
            datetime: disconnectedAt
          })))
        }
      };
    });
    return [h(Data, {
      loading: this.connections.loading && !this.connections.data,
      error: this.connections.error,
      children: h(/** @type {typeof Table<connectionResponse>} */Table, {
        label: "past connection",
        headers: [{
          id: "member",
          label: "Member",
          empty: "Unknown user"
        }, {
          id: "duration",
          label: "Duration",
          empty: "None"
        }, {
          id: "connectedAt",
          label: "Connected at",
          empty: "Never",
          sort: "connected_at"
        }, {
          id: "disconnectedAt",
          label: "Disconnected at",
          empty: "Never",
          sort: "disconnected_at"
        }],
        empty: "This machine has no past connections.",
        rows: rows ?? [],
        paginate: this.paginate.props(this.connections),
        sort: this.sort.props()
      })
    }), __h(WSListener, {
      ws: this.props.ws,
      "for": [msg.ADMIN_CONNECTION_REFRESH],
      on: () => this.connections.fetch()
    })];
  }
}